import Tooltip from '@anm/components/Tooltip';
import Select, { SelectOption } from '@anm/components/select/Select';
import { DEFAULT_DROPDOWN_DELAY } from '@anm/constants/delays';
import isStreamVideo from '@anm/helpers/is/isStreamVideo';
import { getLinkTarget, getUpgradeLinkProps } from '@anm/helpers/upgradeLink';
import useHover from '@anm/hooks/useHover';
import { memo, useCallback, useEffect, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import vtConfig from '../../../../config';
import { projectSelectors } from '../../../../store/modules/project';
import { streamActions } from '../../../../store/modules/stream';
import { userSelectors } from '../../../../store/modules/user';

import SelectWrapper from './Wrapper';
import { useDownloadOptionsDesktop, useDropdownDirection, DownloadProps } from './hooks';

export * from './hooks';

const DownloadDropdown: FC<DownloadProps> = ({ onStreamDownload }) => {
  const dispatch = useDispatch();
  const [isUserFree, uniqueOptions] = useDownloadOptionsDesktop();
  const isPublishedProject = useSelector(projectSelectors.selectIsCurrentProjectPublished);
  const projectId = useSelector(projectSelectors.selectCurrentProjectId);
  const canUserDownloadVideo = useSelector(userSelectors.selectCanUserDownloadVideo);

  const [ref, isHovered, setHovered] = useHover<HTMLDivElement>();
  const onHoverOff = useCallback(() => setHovered(false), []);
  const onHoverOn = useCallback(() => setHovered(true), []);

  const [dropdownDirection, setOptionsHeight] = useDropdownDirection({
    ref,
    isHovered
  });

  const editorUrl = `${vtConfig().urls.editor}/#p=${projectId}&target=publish`;
  const isStream = uniqueOptions.some(option => isStreamVideo(option.value));
  const options = !canUserDownloadVideo && !isStream ? [] : uniqueOptions;
  const isDropdownDisabled = !options.length;
  const isActiveView = !canUserDownloadVideo || !isDropdownDisabled;

  const icon = isActiveView ? 'images/icons/download_icon_blue.svg' : 'images/icons/download_icon_grey_V1.svg';

  const label = {
    icon,
    label: 'Download video and audio',
    value: 'Download video and audio'
  };

  const firstStreamId = useSelector(projectSelectors.selectCurrentProjectFirstStream);

  useEffect(() => {
    if (isStream && firstStreamId) {
      dispatch(streamActions.fetchStream(firstStreamId));
    }
  }, [isStream, firstStreamId]);

  const handleDropdownChange = ({ value }: SelectOption<string>) => {
    value && isStreamVideo(value) && firstStreamId && onStreamDownload(firstStreamId);

    onHoverOff();
  };

  const renderTooltipContent = useCallback(
    () => (
      <>
        {!isPublishedProject ? (
          <span>
            <a href={editorUrl} target={getLinkTarget()}>
              Publish this video
            </a>{' '}
            in the editor to download.
          </span>
        ) : isUserFree ? (
          <span>
            To download your video, please <a {...getUpgradeLinkProps()}>upgrade</a>
          </span>
        ) : null}
      </>
    ),
    [editorUrl]
  );

  const renderTooltip = isDropdownDisabled ? renderTooltipContent : undefined;

  return (
    <SelectWrapper
      count={options.length}
      {...{ ref, isActiveView, dropdownDirection }}
      isHovered={isHovered && !isDropdownDisabled}
    >
      <Tooltip withArrow direction="to-top" delayHide={200} renderContent={renderTooltip}>
        <Select
          value={label}
          name="download"
          options={options}
          openDelay={DEFAULT_DROPDOWN_DELAY}
          openByAction="hover"
          variant="without-border"
          onChange={handleDropdownChange}
          onMouseEnter={onHoverOn}
          onMouseLeave={onHoverOff}
          disabled={isDropdownDisabled}
          onOptionsHeightCalculated={setOptionsHeight}
        />
      </Tooltip>
    </SelectWrapper>
  );
};

export default memo(DownloadDropdown);
